// Nhận đồ vào hành trang (ITEMS §8.4, §9): tự trang bị khi idle (món ▲ không đánh đổi), bộ lọc tự
// phân giải, túi đầy thì phân giải món không được bảo vệ. Hàm thuần; dùng chung cho online, offline, sim.
import { EQUIP_SLOTS, type EquipSlot, type ItemInstance } from '@data/items/types';
import { compareToEquipped, itemPower, type Loadout, type PlayerBase } from './stats';
import { isProtected, matchesFilter, salvageYield, type SalvageFilter } from './nurture';

export type Equipped = Record<EquipSlot, ItemInstance | null>;

export interface GearState {
  equipped: Equipped;
  bag: ItemInstance[];
}

export function emptyEquipped(): Equipped {
  return Object.fromEntries(EQUIP_SLOTS.map((s) => [s, null])) as Equipped;
}

export interface ReceiveCtx {
  base: PlayerBase;
  filter: SalvageFilter;
  playerGiai: number;
  capacity: number;
  autoEquip: boolean;
}

export interface ReceiveReport {
  /**
   * `ratio`: ΔLC toàn thân / LC hiện tại. `itemGain`: LC món mới / LC món cùng ô − 1 (BALANCE §5.4
   * "nâng cấp" = itemGain ≥ 2%); null khi lấp ô trống.
   */
  equipped: { item: ItemInstance; slot: EquipSlot; ratio: number; itemGain: number | null }[];
  stored: ItemInstance[];
  salvagedCount: number;
  /** Trong số đã phân giải: vì túi đầy. */
  salvagedFull: number;
  dust: number;
  refiningStone: number;
}

export function emptyReceiveReport(): ReceiveReport {
  return { equipped: [], stored: [], salvagedCount: 0, salvagedFull: 0, dust: 0, refiningStone: 0 };
}

/** Món đang mặc có đánh đổi nếu bị thay (Đạo Văn / độc bản): idle chỉ gợi ý, không tự thay. */
function hasTradeoff(item: ItemInstance | null): boolean {
  return !!item && (!!item.daoGlyph || !!item.uniqueId);
}

export function receiveItems(
  gear: GearState,
  items: readonly ItemInstance[],
  ctx: ReceiveCtx,
  report: ReceiveReport = emptyReceiveReport(),
): { gear: GearState; report: ReceiveReport } {
  const equipped = { ...gear.equipped };
  const bag = [...gear.bag];
  const queue = [...items];
  while (queue.length) {
    const it = queue.shift()!;
    if (ctx.autoEquip) {
      const cmp = compareToEquipped(ctx.base, equipped as Loadout, it);
      if (cmp.delta > 0 && !hasTradeoff(equipped[cmp.slot])) {
        const old = equipped[cmp.slot];
        equipped[cmp.slot] = it;
        const itemGain = old ? itemPower(ctx.base, it) / Math.max(1e-9, itemPower(ctx.base, old)) - 1 : null;
        report.equipped.push({ item: it, slot: cmp.slot, ratio: cmp.ratio, itemGain });
        if (old) queue.unshift(old);
        continue;
      }
    }
    if (matchesFilter(it, ctx.filter, ctx.playerGiai)) {
      const y = salvageYield(it);
      report.salvagedCount++;
      report.dust += y.dust;
      report.refiningStone += y.refiningStone;
    } else if (bag.length < ctx.capacity || isProtected(it)) {
      bag.push(it);
      report.stored.push(it);
    } else {
      const y = salvageYield(it);
      report.salvagedCount++;
      report.salvagedFull++;
      report.dust += y.dust;
      report.refiningStone += y.refiningStone;
    }
  }
  return { gear: { equipped, bag }, report };
}
